/** necessary relative import because they are imported in the storybook.d */
import type {
  CardDesign,
  Rarity,
  Sport,
} from '#src/__generated__/globalTypes.ts';
import type { LineupStyle } from '#src/components/lobby/ContenderLayout/types.ts';
import type { EngagementScore } from '#src/lib/engagementScore.ts';

import { local } from './index.ts';

export enum SportSelectType {
  ALL_SPORTS = 'allSport',
  FOOTBALL = 'football',
  BASEBALL = 'baseball',
  NBA = 'nba',
}

export type LocalStorageEntries = {
  test: unknown;
  mlbGameViewStats: 'fantasy' | 'actual';
  trackingConsent: boolean;
  sportLineupStyle: Record<Sport, LineupStyle>;
  sport: Sport | undefined;
  /** Language picked explicitly, used on unprefixed urls. */
  preferredLocale: string;
  lastVisitedEventType: string;
  navigationSidebarCollapsed: boolean;
  /** Bare ids are legacy entries, written before dismissals expired. */
  myBidsBannerIgnoredBids: (string | { id: string; endDate: string })[];
  expandLiveEvent: boolean;
  DEVICE_FINGERPRINT: string;
  randomSessionId: string;
  selectedSport: 'football' | 'baseball' | 'nba';
  selectedDesign: CardDesign;
  cardEditionName: string;
  cardBuilderShowPlayerPicture: boolean;
  cardBuilderShowTeamPicture: boolean;
  enableShowAllCardSlots: boolean;
  'gql-debug': boolean;
  'debug-menu-position': { x: number; y: number } | undefined;
  'MySorare.selectedSport': SportSelectType;
  arenaRarityFilter: Rarity | 'all';
  arenaSeePro: boolean;
  REFERRER: string;
  IMPACT_CLICKID: string;
  GOOGLE_CLICKID: string;
  FACEBOOK_CLICKID: string;
  TWITTER_CLICKID: string;
  TIKTOK_CLICKID: string;
  SNAPCHAT_CLICKID: string;
  REDDIT_CLICKID: string;
  INVITATION_TOKEN: string;
  UTM_SOURCE: string;
  UTM_MEDIUM: string;
  UTM_CAMPAIGN: string | number | undefined;
  UTM_TERM: string;
  UTM_CONTENT: string;
  CUSTOM_PROFILE_ID: string;
  TRAFFIC_CATEGORY: string;
  PARTNER: string;
  USER: string;
  ONBOARDING_PRO: string | undefined;
  REFERRER_CODE: string;
  REFERRER_SLUG: string;
  REFERRER_IS_AFFILIATE: boolean;
  jonumGracePeriodWarningDismissedAt: number;
  fiatRewardCurrencyChosenForTask: string;
  dailyRewardsCalendarSeenStepId: string;
  /** Claimable steps whose target hit celebration was seen, keyed by sport. */
  targetHitCelebrationSeenStepIds: Partial<Record<Sport, string[]>>;
  /** Board task state already animated, keyed by task id. */
  seenTaskStates: Record<string, string>;
  /** Last hub activity highlighted on the series board, keyed by sport. */
  seriesHubAnimatedActivityIds: Partial<Record<Sport, string>>;
  /** User last logged into the Zendesk Messenger from this browser. */
  zendeskMessengerUserId: string;
  /** Android TWA only: drives when the Play in-app review is requested. */
  engagementScore: EngagementScore;
};
export const localStorage = {
  getItem: <K extends keyof LocalStorageEntries>(value: K) =>
    local.getItem(value) as LocalStorageEntries[K],
  getStringItem: <K extends keyof LocalStorageEntries>(value: K) =>
    local.getItem(value)?.toString() as LocalStorageEntries[K],
  setItem: <K extends keyof LocalStorageEntries>(
    key: K,
    value: LocalStorageEntries[K] | null
  ) => local.setItem(key, value),
  removeItem: <K extends keyof LocalStorageEntries>(key: K) =>
    local.removeItem(key),
  key: (index: number) => local.key(index),
  length: local.length,
  clear: local.clear,
};
